import { ControlledField } from 'remote/global';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mui/system';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { Button } from '@mui/material';
import { useUsersPasswordMutation } from 'api/queries/users';
import PaperLayout from '../../PaperLayout';
import { PasswordSchemaType, basePasswordSchema } from './types';

function AccountChangePasswordForm() {
  const tInstance = useTranslation();
  const { t } = tInstance;
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValidating, isLoading },
    reset,
  } = useForm<PasswordSchemaType>({
    defaultValues: {
      password: '',
      confirmPassword: '',
      newPassword: '',
    },
    resolver: zodResolver(basePasswordSchema),
  });
  const updatePasswordMutation = useUsersPasswordMutation();

  const onValid = async (data: PasswordSchemaType) => {
    try {
      const status = await updatePasswordMutation.mutateAsync(data);
      if (status === 200) {
        toast.success(t('Toast.success'));
        // Reset the form
        reset();
      }
    } catch (err) {
      toast.error(t('Toast.failure'));
    }
  };

  return (
    <PaperLayout
      title={t('SettingsView.password.title')}
      subtitle={t('SettingsView.password.subtitle')}
    >
      <form onSubmit={handleSubmit(onValid)}>
        <Stack direction='column' spacing={1}>
          <ControlledField
            control={control}
            name='password'
            translationKey='currentPassword'
            fieldProps={{
              InputProps: {
                type: 'password',
                autoComplete: 'password',
              },
            }}
          />
          <ControlledField
            control={control}
            name='newPassword'
            fieldProps={{
              InputProps: {
                type: 'password',
                autoComplete: 'new-password',
              },
            }}
          />
          <ControlledField
            control={control}
            name='confirmPassword'
            fieldProps={{
              InputProps: {
                type: 'password',
                autoComplete: 'new-password',
              },
            }}
          />
        </Stack>
        <Button
          type='submit'
          disabled={isSubmitting || isValidating || isLoading}
          variant='contained'
          fullWidth
          sx={{ mt: '2rem' }}
        >
          {t('actions.user.changePassword')}
        </Button>
      </form>
    </PaperLayout>
  );
}

export default AccountChangePasswordForm;
